import { definePreset } from '@primeng/themes';
import Aura from '@primeng/themes/aura';


const WMPreset = definePreset(Aura, {
    primitive: {
        borderRadius: {
            none: "0",
            xs: "2px",
            sm: "4px",
            md: "6px",
            lg: "8px",
            xl: "12px"
        },
        green: {
            50: "#faffff",
            100: "#faffff",
            200: "#f0fff5",
            300: "#e6faeb",
            400: "#8cfacd",
            500: "#55e6aa",
            600: "#00c878",
            700: "#14966e",
            800: "#0a6e50",
            900: "#003c1e",
            950: "#003c1e"
        },
        red: {
            50: "#fff5f5",
            100: "#fff5f5",
            200: "#ffdceb",
            300: "#ffc3d7",
            400: "#ff96b4",
            500: "#e64678",
            600: "#dc0050",
            700: "#a00032",
            800: "#6e0028",
            900: "#500014",
            950: "#500014"
        },
        yellow: {
            50: "#fffffa",
            100: "#fffffa",
            200: "#faf5eb",
            300: "#faebcd",
            400: "#fae6aa",
            500: "#f0d26e",
            600: "#f0c850",
            700: "#aa8c1e",
            800: "#786414",
            900: "#463c00",
            950: "#463c00"
        },
        blue: {
            50: "#f5faff",
            100: "#f5faff",
            200: "#ebf5fa",
            300: "#d2ebff",
            400: "#96d2ff",
            500: "#50b4ff",
            600: "#1496e6",
            700: "#146eb4",
            800: "#14466e",
            900: "#143246",
            950: "#143246"
        },
        purple: {
            50: "#faf5ff",
            100: "#faf5ff",
            200: "#f0e1f4",
            300: "#e6cdfa",
            400: "#dcb4ff",
            500: "#be82ff",
            600: "#aa50ff",
            700: "#8228d2",
            800: "#5a2882",
            900: "#321450",
            950: "#321450"
        },
        gray: {
            50: "#fafaff",
            100: "#fafaff",
            200: "#fafafa",
            300: "#f0f0f5",
            400: "#dcdce1",
            500: "#a0a0aa",
            600: "#6e6e7e",
            700: "#505055",
            800: "#37373c",
            900: "#1e1e23",
            950: "#1e1e23"
        },
    },
    semantic: {
        transitionDuration: "0.2s",
        focusRing: {
            width: "1px",
            style: "solid",
            color: "{primary.color}",
            offset: "2px",
            shadow: "none"
        },
        disabledOpacity: "0.6",
        iconSize: "1rem",
        anchorGutter: "2px",
        primary: {
            50: "#f5faff",
            100: "#f5faff",
            200: "#ebf5fa",
            300: "#d2ebff",
            400: "#96d2ff",
            500: "#50b4ff",
            600: "#1496e6",
            700: "#146eb4",
            800: "#14466e",
            900: "#143246",
            950: "#143246"
        },
        formField: {
            paddingX: "0.75rem",
            paddingY: "0.5rem",
            sm: {
                fontSize: "0.875rem",
                paddingX: "0.625rem",
                paddingY: "0.375rem"
            },
            lg: {
                fontSize: "1.125rem",
                paddingX: "0.875rem",
                paddingY: "0.625rem"
            },
            borderRadius: "{border.radius.md}",
            focusRing: {
                width: "0",
                style: "none",
                color: "transparent",
                offset: "0",
                shadow: "none"
            },
            transitionDuration: "{transition.duration}"
        },
        list: {
            padding: "0.25rem 0.25rem",
            gap: "2px",
            header: {
                padding: "0.5rem 1rem 0.25rem 1rem"
            },
            option: {
                padding: "0.5rem 0.75rem",
                borderRadius: "{border.radius.sm}"
            },
            optionGroup: {
                padding: "0.5rem 0.75rem",
                fontWeight: "600"
            }
        },
        content: {
            borderRadius: "{border.radius.md}"
        },
        mask: {
            transitionDuration: "0.15s"
        },
        navigation: {
            list: {
                padding: "0.25rem 0.25rem",
                gap: "2px"
            },
            item: {
                padding: "0.5rem 0.75rem",
                borderRadius: "{border.radius.sm}",
                gap: "0.5rem"
            },
            submenuLabel: {
                padding: "0.5rem 0.75rem",
                fontWeight: "600"
            },
            submenuIcon: {
                size: "0.875rem"
            }
        },
        overlay: {
            select: {
                borderRadius: "{border.radius.md}",
                shadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)"
            },
            popover: {
                borderRadius: "{border.radius.md}",
                padding: "0.75rem",
                shadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)"
            },
            modal: {
                borderRadius: "{border.radius.xl}",
                padding: "1.25rem",
                shadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)"
            },
            navigation: {
                shadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)"
            }
        },
        colorScheme: {
            light: {
                surface: {
                    0: "#ffffff",
                    50: "#f8fafc",
                    100: "#f1f5f9",
                    200: "#e2e8f0",
                    300: "#cbd5e1",
                    400: "#94a3b8",
                    500: "#64748b",
                    600: "#475569",
                    700: "#334155",
                    800: "#1e293b",
                    900: "#0f172a",
                    950: "#020617"
                },
                primary: {
                    color: "{primary.700}",
                    contrastColor: "#ffffff",
                    hoverColor: "{primary.600}",
                    activeColor: "{primary.700}"
                },
                highlight: {
                    background: "{primary.50}",
                    focusBackground: "{primary.100}",
                    color: "{primary.700}",
                    focusColor: "{primary.800}"
                },
                mask: {
                    background: "rgba(0,0,0,0.4)",
                    color: "{surface.200}"
                },
                formField: {
                    background: "{surface.0}",
                    disabledBackground: "{surface.200}",
                    filledBackground: "{surface.50}",
                    filledHoverBackground: "{surface.50}",
                    filledFocusBackground: "{surface.50}",
                    borderColor: "{surface.300}",
                    hoverBorderColor: "{surface.400}",
                    focusBorderColor: "{primary.color}",
                    invalidBorderColor: "{red.400}",
                    color: "{surface.700}",
                    disabledColor: "{surface.500}",
                    placeholderColor: "{surface.500}",
                    invalidPlaceholderColor: "{red.600}",
                    floatLabelColor: "{surface.500}",
                    floatLabelFocusColor: "{primary.600}",
                    floatLabelActiveColor: "{surface.500}",
                    floatLabelInvalidColor: "{form.field.invalid.placeholder.color}",
                    iconColor: "{surface.400}",
                    shadow: "0 0 #0000, 0 0 #0000, 0 1px 2px 0 rgba(18, 18, 23, 0.05)"
                },
                text: {
                    color: "{surface.700}",
                    hoverColor: "{surface.800}",
                    mutedColor: "{surface.500}",
                    hoverMutedColor: "{surface.600}"
                },
                content: {
                    background: "{surface.0}",
                    hoverBackground: "{surface.100}",
                    borderColor: "{surface.200}",
                    color: "{text.color}",
                    hoverColor: "{text.hover.color}"
                },
                overlay: {
                    select: {
                        background: "{surface.0}",
                        borderColor: "{surface.200}",
                        color: "{text.color}"
                    },
                    popover: {
                        background: "{surface.0}",
                        borderColor: "{surface.200}",
                        color: "{text.color}"
                    },
                    modal: {
                        background: "{surface.0}",
                        borderColor: "{surface.200}",
                        color: "{text.color}"
                    }
                },
                list: {
                    option: {
                        focusBackground: "{surface.100}",
                        selectedBackground: "{highlight.background}",
                        selectedFocusBackground: "{highlight.focus.background}",
                        color: "{text.color}",
                        focusColor: "{text.hover.color}",
                        selectedColor: "{highlight.color}",
                        selectedFocusColor: "{highlight.focus.color}",
                        icon: {
                            color: "{surface.400}",
                            focusColor: "{surface.500}"
                        }
                    },
                    optionGroup: {
                        background: "transparent",
                        color: "{text.muted.color}"
                    }
                },
                navigation: {
                    item: {
                        focusBackground: "{surface.100}",
                        activeBackground: "{surface.100}",
                        color: "{text.color}",
                        focusColor: "{text.hover.color}",
                        activeColor: "{text.hover.color}",
                        icon: {
                            color: "{surface.400}",
                            focusColor: "{surface.500}",
                            activeColor: "{surface.500}"
                        }
                    },
                    submenuLabel: {
                        background: "transparent",
                        color: "{text.muted.color}"
                    },
                    submenuIcon: {
                        color: "{surface.400}",
                        focusColor: "{surface.500}",
                        activeColor: "{surface.500}"
                    }
                }
            },
            dark: {
                surface: {
                    0: "#ffffff",
                    50: "#fafafa",
                    100: "#f4f4f5",
                    200: "#e4e4e7",
                    300: "#d4d4d8",
                    400: "#a1a1aa",
                    500: "#71717a",
                    600: "#52525b",
                    700: "#3f3f46",
                    800: "#27272a",
                    900: "#18181b",
                    950: "#09090b"
                },
                primary: {
                    color: "{primary.400}",
                    contrastColor: "{surface.900}",
                    hoverColor: "{primary.300}",
                    activeColor: "{primary.200}"
                },
                highlight: {
                    background: "color-mix(in srgb, {primary.400}, transparent 84%)",
                    focusBackground: "color-mix(in srgb, {primary.400}, transparent 76%)",
                    color: "rgba(255,255,255,.87)",
                    focusColor: "rgba(255,255,255,.87)"
                },
                mask: {
                    background: "rgba(0,0,0,0.6)",
                    color: "{surface.200}"
                },
                formField: {
                    background: "{surface.950}",
                    disabledBackground: "{surface.700}",
                    filledBackground: "{surface.800}",
                    filledHoverBackground: "{surface.800}",
                    filledFocusBackground: "{surface.800}",
                    borderColor: "{surface.600}",
                    hoverBorderColor: "{surface.500}",
                    focusBorderColor: "{primary.color}",
                    invalidBorderColor: "{red.300}",
                    color: "{surface.0}",
                    disabledColor: "{surface.400}",
                    placeholderColor: "{surface.400}",
                    invalidPlaceholderColor: "{red.400}",
                    floatLabelColor: "{surface.400}",
                    floatLabelFocusColor: "{primary.color}",
                    floatLabelActiveColor: "{surface.400}",
                    floatLabelInvalidColor: "{form.field.invalid.placeholder.color}",
                    iconColor: "{surface.400}",
                    shadow: "0 0 #0000, 0 0 #0000, 0 1px 2px 0 rgba(18, 18, 23, 0.05)"
                },
                text: {
                    color: "{surface.0}",
                    hoverColor: "{surface.0}",
                    mutedColor: "{surface.400}",
                    hoverMutedColor: "{surface.300}"
                },
                content: {
                    background: "{surface.900}",
                    hoverBackground: "{surface.800}",
                    borderColor: "{surface.700}",
                    color: "{text.color}",
                    hoverColor: "{text.hover.color}"
                },
                overlay: {
                    select: {
                        background: "{surface.900}",
                        borderColor: "{surface.700}",
                        color: "{text.color}"
                    },
                    popover: {
                        background: "{surface.900}",
                        borderColor: "{surface.700}",
                        color: "{text.color}"
                    },
                    modal: {
                        background: "{surface.900}",
                        borderColor: "{surface.700}",
                        color: "{text.color}"
                    }
                },
                list: {
                    option: {
                        focusBackground: "{surface.800}",
                        selectedBackground: "{highlight.background}",
                        selectedFocusBackground: "{highlight.focus.background}",
                        color: "{text.color}",
                        focusColor: "{text.hover.color}",
                        selectedColor: "{highlight.color}",
                        selectedFocusColor: "{highlight.focus.color}",
                        icon: {
                            color: "{surface.500}",
                            focusColor: "{surface.400}"
                        }
                    },
                    optionGroup: {
                        background: "transparent",
                        color: "{text.muted.color}"
                    }
                },
                navigation: {
                    item: {
                        focusBackground: "{surface.800}",
                        activeBackground: "{surface.800}",
                        color: "{text.color}",
                        focusColor: "{text.hover.color}",
                        activeColor: "{text.hover.color}",
                        icon: {
                            color: "{surface.500}",
                            focusColor: "{surface.400}",
                            activeColor: "{surface.400}"
                        }
                    },
                    submenuLabel: {
                        background: "transparent",
                        color: "{text.muted.color}"
                    },
                    submenuIcon: {
                        color: "{surface.500}",
                        focusColor: "{surface.400}",
                        activeColor: "{surface.400}"
                    }
                }
            }
        }
    },
    components: {
        toast: {
            colorScheme: {
                light: {
                    icon: {
                        size: '24px'
                    },
                    info: {
                        background: '{blue.400}',
                        color: '{text.color}',
                        close: { //This is not functional right now
                            button: {
                                hover: {
                                    background: '{blue.400}'
                                }
                            }
                        },
                        border: {
                            color: '{blue.400}'
                        }
                    },
                    warn: {
                        background: '{yellow.400}',
                        color: '{text.color}',
                        border: {
                            color: '{yellow.400}'
                        }
                    },
                    error: {
                        background: '{red.300}',
                        color: '{text.color}',
                        border: {
                            color: '{red.300}'
                        }
                    }
                },
                dark: {
                    icon: {
                        size: '2rem'
                    },
                    info: {
                        background: '{blue.400}',
                        color: '{surface.700}',
                        detail: {
                            color: '{surface.700}'
                        }
                    },
                    warn: {
                        background: '{yellow.400}',
                        color: '{surface.700}',
                        detail: {
                            color: '{surface.700}'
                        }
                    },
                    error: {
                        background: '{red.300}',
                        color: '{surface.700}',
                        detail: {
                            color: '{surface.700}'
                        }
                    }
                }
            }
        },
        button: {
            colorScheme: {
                light: {
                    primary: {
                        hover: {
                            color: '{blue.200}',
                            background: '{blue.800}'
                        }
                    },
                    secondary: {
                        color: '{blue.600}',
                        background: '{blue.200}',
                        hover: {
                            color: '{surface.0}',
                            background: '{blue.700}',
                        },
                    },
                    text: {
                        primary: {
                            color: '{blue.600}',
                            background: '{transparent}',
                            hover: {
                                color: '{blue.600}',
                                background: '{blue.200}',
                            },
                        },
                    },
                    outlined: {
                        primary: {
                            color: '{blue.700}',
                            background: '{transparent}',
                            borderColor: '{blue.700}',
                            hover: {
                                background: '{blue.200}',
                            },
                        },

                    }
                },
                dark: {
                    secondary: {
                        color: '{surface.50}',
                        background: '{surface.600}',
                        hover: {
                            color: '{surface.50}',
                            background: '{blue.700}',
                        },
                    },
                    success: {
                        color: '{surface.50}',
                        background: '{green.600}',
                        borderColor: '{green.600}',
                        hover: {
                            color: '{green.800}',
                            background: '{green.400}',
                            borderColor: '{green.400}',
                        },
                    },
                    warn: {
                        color: '{yellow.950}',
                        background: '{yellow.600}',
                        borderColor: '{yellow.600}',
                        hover: {
                            color: '{yellow.950}',
                            background: '{yellow.400}',
                            borderColor: '{yellow.400}',
                        },
                    }
                }
            }
        },
        checkbox: {
            root: {
                height: '22px',
                width: '22px',
            },
            sm: {
                height: '18px',
                width: '18px',
            },
            colorScheme: {
                light: {
                    checked: {
                        hover: {
                            background: '{blue.800}'
                        }
                    },
                },
                dark: {
                    checked: {
                        hover: {
                            background: '{blue.300}'
                        }
                    },
                }
            }
        },
        toggleswitch: {
            root: {
                height: '22px',
                width: '36px',
            }
        },
        progressbar: {
            root: {
                height: '6px',
            },
            colorScheme: {
                light: {
                    value: {
                        background: '{blue.600}'
                    }
                }

            }
        },
    }
});


export {WMPreset};
